import {
  Breadcrumbs,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  InlineStack,
  Link,
  List,
  ListItem,
  Page,
  PageBody,
  PageBreadcrumbs,
  PageHeader,
  PageHeaderDescription,
  PageHeaderTitle,
  Panel,
  PanelDescription,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Select,
  Stack,
  Text,
  TextField,
  TextLabel,
  useModals,
} from '@customerio/pluma-components/react';
import { Navigate } from 'react-router-dom';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';
import { getWorkspaceIdFromCurrentUrl, useCurrentWorkspace } from '@/context/current-workspace';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { servicesQuery } from '@/services/api';
import { UnsuppressConfirmModal } from './components/unsuppress-confirm-modal';
import type { IdentifierType } from './types';
import { z } from 'zod';

const RESOURCE_SECTIONS = [
  {
    title: 'Documentation',
    items: [
      { label: 'Email suppression', href: 'https://docs.customer.io/journeys/esp-suppression/' },
      {
        label: 'Privacy suppression',
        href: 'https://docs.customer.io/journeys/deleting-users/#how-suppressing-ids-works',
      },
    ],
  },
  {
    title: 'Legal',
    items: [
      { label: 'GDPR at Customer.io', href: 'https://customer.io/legal/gdpr' },
      { label: 'CCPA at Customer.io', href: 'https://customer.io/legal/ccpa' },
    ],
  },
  {
    title: 'Settings',
    items: [{ label: 'Privacy & Data Account Settings', href: '/settings/privacy' }],
  },
] as const;

const MAX_IDENTIFIERS_PER_REQUEST = 100;

const emailSchema = z.string().email('Enter a valid email address');
const idSchema = z.string().min(1, 'Enter a customer ID');
function parseCommaSeparatedValues(input: string): string[] {
  return input
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function validateIdentifiers(
  values: string[],
  type: IdentifierType,
): { success: true; values: string[] } | { success: false; error: string } {
  if (values.length === 0) {
    return {
      success: false,
      error: 'Enter at least one identifier.',
    };
  }
  if (values.length > MAX_IDENTIFIERS_PER_REQUEST) {
    return {
      success: false,
      error: `Maximum ${MAX_IDENTIFIERS_PER_REQUEST} identifiers per request. You entered ${values.length}.`,
    };
  }
  const schema = type === 'email' ? emailSchema : idSchema;
  const errors: string[] = [];
  for (let i = 0; i < values.length; i++) {
    const result = schema.safeParse(values[i]);
    if (!result.success) {
      errors.push(`Value ${i + 1}: ${result.error.errors[0]?.message ?? 'Invalid'}`);
    }
  }
  if (errors.length > 0) {
    return { success: false, error: errors[0] ?? 'Invalid input' };
  }
  return { success: true, values };
}

export function WorkspaceSuppressionsPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const currentWorkspace = useCurrentWorkspace();
  const { hasPermission } = usePermissions();
  const modals = useModals();

  const [identifierType, setIdentifierType] = useState<IdentifierType>('email');
  const [identifier, setIdentifier] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>();

  const canViewSuppressions = hasPermission('journeys/people/view') === true;
  const canUnsuppress = hasPermission('journeys/people/edit') === true;

  const {
    data: suppressionCountData,
    isError: isSuppressionCountError,
    isLoading: isSuppressionCountLoading,
  } = servicesQuery.getSuppressionCount.useQuery({
    queryKey: ['suppressionCount', workspaceId],
    queryData: { params: { workspaceId: workspaceId ?? 0 } },
    enabled: workspaceId != null && workspaceId > 0 && canViewSuppressions,
    retry: false,
  });

  // Only show count when successfully loaded — hide on error to avoid misleading "0 suppressed profiles"
  const suppressionCount = suppressionCountData?.body?.count;
  const hasSuppressionCountData =
    !isSuppressionCountLoading && !isSuppressionCountError && suppressionCount !== undefined;
  const trimmedIdentifier = identifier.trim();
  const isIdentifierEmpty = isEmptyString(trimmedIdentifier);
  const workspaceName = currentWorkspace?.name ?? 'this workspace';

  const openConfirmModal = useCallback(() => {
    if (workspaceId == null || workspaceId <= 0 || isIdentifierEmpty) return;

    const values = parseCommaSeparatedValues(trimmedIdentifier);
    const result = validateIdentifiers(values, identifierType);
    if (!result.success) {
      setValidationError(result.error);
      return;
    }
    setValidationError(undefined);

    const modalId = modals.open(UnsuppressConfirmModal, {
      identifiers: result.values,
      workspaceName,
      workspaceId,
      identifierType,
    });
    void modals.waitForClose(modalId).then((modalResult) => {
      if (modalResult.reason === 'confirm') {
        setIdentifier('');
      }
    });
  }, [workspaceId, trimmedIdentifier, isIdentifierEmpty, identifierType, workspaceName, modals]);

  if (workspaceId == null || workspaceId <= 0) {
    return <Navigate to="/settings" replace={true} />;
  }

  const emailSuppressionListHref = `/workspaces/${workspaceId}/settings/actions/email/suppression_list`;

  const breadcrumbs = [
    { text: 'Workspace settings', href: `/workspaces/${workspaceId}/settings` },
    { text: 'Workspace Suppressions' },
  ];

  return (
    <>
      <Helmet>
        <title>Workspace Suppressions | Customer.io</title>
      </Helmet>
      <Page maxW="full" w="full">
        <PageBreadcrumbs>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </PageBreadcrumbs>
        <PageHeader>
          <PageHeaderTitle>Workspace Suppressions</PageHeaderTitle>
          <PageHeaderDescription>
            Suppressions in your workspace can occur in two ways, email suppressions or privacy suppressions.
          </PageHeaderDescription>
        </PageHeader>
        <PageBody>
          <Box pt="200" pr="400" pb="400" pl="0" w="full">
            <Flex gap="800" alignItems="stretch" w="full">
              <Box flex={1} minW="0">
                <Stack gap="400">
                  <Panel>
                    <PanelHeader>
                      <Stack gap="100">
                        <InlineStack gap="100" alignBlock="center">
                          <Icon name="email" size="sm" sizeVersion="v2" color="subtle" />
                          <PanelTitle level="3">Email suppression</PanelTitle>
                        </InlineStack>
                        <PanelDescription>
                          Your email service provider (ESP) maintains a list of suppressed email addresses, preventing
                          you from messaging people who have experienced a hard bounce or tagged a spam complaint.
                        </PanelDescription>
                      </Stack>
                    </PanelHeader>
                    <PanelSection>
                      <Stack gap="200">
                        <Flex gap="200" alignItems="center" justifyContent="space-between" w="full">
                          <Heading level="4" text="product-h4">
                            Email suppression list
                          </Heading>
                          <Link href={emailSuppressionListHref} variant="primary" flexShrink="0">
                            View list
                          </Link>
                        </Flex>
                        <Paragraph variant="secondary">
                          If you manage email deliveries through Customer.io, you can view and remove emails from the
                          list in <Link href={emailSuppressionListHref}>Email settings</Link>. If you use a Custom SMTP
                          server, you must manage your email provider&apos;s suppression list directly through the ESP.
                        </Paragraph>
                      </Stack>
                    </PanelSection>
                  </Panel>

                  <Panel>
                    <PanelHeader>
                      <Stack gap="100">
                        <InlineStack gap="100" alignBlock="center">
                          <Icon name="database" size="sm" sizeVersion="v2" color="subtle" />
                          <PanelTitle level="3">Privacy suppression</PanelTitle>
                        </InlineStack>
                        <PanelDescription>
                          Privacy suppression prevents a person or any data related to them from being added to the
                          workspace. People are added to the list when the suppression option is selected during
                          deletion, generally to comply with GDPR/CCPA guidelines.
                        </PanelDescription>
                      </Stack>
                    </PanelHeader>
                    <PanelSection>
                      <Stack gap="200">
                        <Heading level="4" text="product-h4">
                          Privacy suppression list removal
                        </Heading>
                        {canViewSuppressions ? (
                          isSuppressionCountLoading ? (
                            <Paragraph variant="secondary">Loading suppression count…</Paragraph>
                          ) : isSuppressionCountError ? (
                            <Paragraph variant="secondary">Unable to load suppression count.</Paragraph>
                          ) : hasSuppressionCountData ? (
                            <Paragraph variant="secondary">
                              You currently have{' '}
                              <Text weight="bold" as="span">
                                {suppressionCount}
                              </Text>{' '}
                              suppressed
                              {suppressionCount === 1 ? ' profile' : ' profiles'}.
                            </Paragraph>
                          ) : null
                        ) : null}
                        <Stack gap="200" pt="200">
                          {canUnsuppress ? (
                            <Stack gap="200">
                              <Paragraph variant="secondary">
                                Allow a previously{' '}
                                <Link
                                  href="https://docs.customer.io/journeys/deleting-users/#how-suppressing-ids-works"
                                  variant="secondary"
                                >
                                  suppressed
                                </Link>{' '}
                                email or ID to be used again in this workspace. The person can be re-added with no prior
                                history.
                              </Paragraph>
                              <Flex gap="100" alignItems="flex-end" pt="200" w="full">
                                <Box pb="400" flexShrink="0">
                                  <Select
                                    label="Identifier type"
                                    value={identifierType}
                                    onChange={(value) => {
                                      setIdentifierType((value ?? 'email') as IdentifierType);
                                      setIdentifier('');
                                      setValidationError(undefined);
                                    }}
                                    options={[
                                      { value: 'email', label: 'Email' },
                                      { value: 'id', label: 'Customer ID' },
                                    ]}
                                  />
                                </Box>
                                <Box flex={1} minW="0" pb="400">
                                  <Box position="relative">
                                    <TextField
                                      ariaLabel={
                                        identifierType === 'email'
                                          ? 'Emails to unsuppress (comma-separated)'
                                          : 'Customer IDs to unsuppress (comma-separated)'
                                      }
                                      placeholder={
                                        identifierType === 'email'
                                          ? 'Enter email or emails separated by commas'
                                          : 'Enter customer ID or IDs, comma-separated'
                                      }
                                      value={identifier}
                                      onInput={(e) => {
                                        setIdentifier(e.target.value);
                                        setValidationError(undefined);
                                      }}
                                      isInvalid={!isEmptyString(validationError)}
                                    />
                                    {!isEmptyString(validationError) ? (
                                      <span className={styles.errorMessage}>{validationError}</span>
                                    ) : null}
                                  </Box>
                                </Box>
                                <Box pb="400" flexShrink="0">
                                  <Button
                                    variant="primary"
                                    onClick={openConfirmModal}
                                    isDisabled={isIdentifierEmpty}
                                    flexShrink="0"
                                  >
                                    {isIdentifierEmpty || parseCommaSeparatedValues(trimmedIdentifier).length === 1
                                      ? 'Search for a profile'
                                      : 'Search for profiles'}
                                  </Button>
                                </Box>
                              </Flex>
                            </Stack>
                          ) : (
                            <Paragraph variant="secondary" pt="100">
                              You need the People edit permission to unsuppress identifiers in this workspace.
                            </Paragraph>
                          )}
                        </Stack>
                      </Stack>
                    </PanelSection>
                  </Panel>
                </Stack>
              </Box>

              <Panel className={styles.resourcesPanel}>
                <PanelHeader>
                  <Stack gap="100">
                    <InlineStack gap="100" alignBlock="center">
                      <Icon name="tip" size="lg" sizeVersion="v2" color="subtle" />
                      <PanelTitle level="3" text="product-h3">
                        Resources
                      </PanelTitle>
                    </InlineStack>
                    <PanelDescription>
                      Below are some useful tips and resources for managing suppressions in your workspace.
                    </PanelDescription>
                  </Stack>
                </PanelHeader>
                <PanelSection>
                  <Stack gap="300">
                    {RESOURCE_SECTIONS.map(({ title, items }) => (
                      <Box key={title}>
                        <TextLabel as="h4" mb="100">
                          {title.toUpperCase()}
                        </TextLabel>
                        <List type="unordered" styleType="none" gap="200">
                          {items.map(({ label, href }) => (
                            <ListItem key={label}>
                              <Link href={href} isExternal={href.startsWith('http')}>
                                {label}
                              </Link>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    ))}
                  </Stack>
                </PanelSection>
              </Panel>
            </Flex>
          </Box>
        </PageBody>
      </Page>
    </>
  );
}
